import { useCallback, useEffect, useState } from 'react'

import type { NDParamsType } from 'commons'
import { usePermissions } from 'commons/customHooks/useAuth'
import useParams from 'commons/customHooks/useParams'
import { ACTIONS_SCREENS, SCREENS } from 'commons/utils/permissions'
import { splittingString } from 'commons/utils/string'
import {
  Alert,
  BatchSearch,
  Icon,
  Info,
  Input,
  Modal,
  Multiselect,
  type OptionProps,
  SlimButton,
  Toggle,
} from 'components'
import type { ActionPerm } from 'components/Structures/Header/constants'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import type { SaleRegion } from 'services/salesOrder/types'
import { setSortFilter } from 'store/NegotiationsContext/negotiationsSlice'
import { useDispatch, useSelector } from 'store/store'

import ModalReport from '../ModalReport'
import * as S from './styles'

type FilterProps = {
  onSubmit: (data: NDParamsType) => void
  getSearchParams: () => NDParamsType
  optionsMesCota: string[]
  optionsOffices: string[]
  optionsOvTypes: string[]
  optionsSalesTeam: OptionProps[]
  optionsSalesRegion: SaleRegion[]
  defaultValues: NDParamsType
  ovTypes: string[]
}

export const Filter = ({
  onSubmit,
  getSearchParams,
  optionsMesCota,
  optionsOffices,
  optionsOvTypes,
  optionsSalesRegion,
  optionsSalesTeam,
  defaultValues,
  ovTypes,
}: FilterProps) => {
  const dispatch = useDispatch()
  const { verifyPermission } = usePermissions()
  const { loadingReport } = useSelector((store) => store.negotiations)

  const [filterExpanded, setFilterExpanded] = useState(false)
  const [disabledHierarquia, setDisabledHierarquia] = useState(true)
  const [openReportModal, setOpenReportModal] = useState(false)
  const [openHistoryRequestModal, setOpenHistoryRequestModal] = useState(false)

  const { createParamsByObject, clearParamsExcept } = useParams()

  const disabledsButtons = [] as string[]

  const methods = useForm({
    defaultValues: getSearchParams(),
  })
  const { control, reset, getValues, setValue, handleSubmit, watch } = methods
  const codSapClie = watch('codigosSapCliente')
  const OVNumbers = watch('numerosOrdemVenda')

  const shouldDisable = useCallback(
    (action: ActionPerm) => !verifyPermission(SCREENS.ND_MANAGE, action),
    [verifyPermission],
  )

  useEffect(() => {
    setFilterExpanded(getSearchParams().filterExpanded === 'true')
  }, [])

  useEffect(() => {
    if (codSapClie !== '') setDisabledHierarquia(false)
    else {
      setDisabledHierarquia(true)
      setValue('hierarquiaCliente', 'false')
    }
  }, [codSapClie])

  useEffect(() => {
    reset(getSearchParams())
  }, [optionsMesCota])

  useEffect(() => {
    if (OVNumbers !== '') {
      reset({ ...defaultValues, numerosOrdemVenda: OVNumbers })
    }
  }, [OVNumbers])

  const handleClearFilter = () => {
    clearParamsExcept(['filterExpanded'])
    reset(defaultValues)
    dispatch(setSortFilter({}))
  }

  const labelField = (label: string, name: string, disableLabel?: boolean) => (
    <S.LabelFilter disabled={disabledsButtons.includes(name) || disableLabel}>
      {label}
      {disabledsButtons.includes(name) && (
        <Info>Campo indisponível. Em breve mais informações.</Info>
      )}
    </S.LabelFilter>
  )

  const handleFilterExpand = () => {
    setFilterExpanded((oldValue) => {
      const newValue = !oldValue
      createParamsByObject({ ...getSearchParams(), filterExpanded: newValue.toString() })
      return newValue
    })
  }

  const filteredOptionsSalesRegion = () => {
    const codEquipe = watch('codigosEquipeVenda')
    if (codEquipe) {
      const cdEquipe = codEquipe.split(',')?.map((item) => item.substring(0, 3))

      return optionsSalesRegion
        .filter((item) => item?.cd_equi_vend?.split(',').some((item) => cdEquipe.includes(item)))
        ?.map((region) => ({
          title: region.ds_regi_vend,
          value: region.cd_regi_vend,
        }))
    }

    return optionsSalesRegion.map((region) => ({
      title: region.ds_regi_vend,
      value: region.cd_regi_vend,
    }))
  }

  const formattingData = (data: NDParamsType) => {
    createParamsByObject({ ...data, filterExpanded: filterExpanded.toString() })
    onSubmit(data)
  }

  useEffect(() => {
    setValue('tiposOrdemVenda', ovTypes.join(','))
  }, [ovTypes])

  return (
    <S.CardFilter>
      <FormProvider {...methods}>
        <S.Form onSubmit={handleSubmit(formattingData)}>
          <S.AccordionFilter expanded={filterExpanded} onChange={handleFilterExpand}>
            <S.FormHeader>
              <S.WrapFormTitle>
                <S.FormTitle>NDs pendentes</S.FormTitle>
                <SlimButton
                  content="Baixar"
                  disabled={shouldDisable(ACTIONS_SCREENS.DOWNLOAD)}
                  icon={<Icon icon="download" size="15" />}
                  loading={loadingReport}
                  onClick={() => setOpenReportModal(true)}
                  type="button"
                  variant="white"
                />
              </S.WrapFormTitle>
              <S.FormActions>
                <S.AccordionButton>
                  <S.TextAccordionButton>Filtros</S.TextAccordionButton>
                </S.AccordionButton>
              </S.FormActions>
            </S.FormHeader>
            <S.FormContent>
              <S.AccordionContent>
                <S.FormRow>
                  <S.FormColumn>
                    {labelField('Equipe de vendas', 'codigosEquipeVenda')}
                    <Controller
                      control={control}
                      name="codigosEquipeVenda"
                      render={({ field }) => (
                        <Multiselect
                          {...field}
                          onChange={(value) => {
                            field.onChange(value.join())
                            const salesRegion = getValues('codigosRegiaoVenda')?.split(',')
                            const newRegion = salesRegion?.filter((item) =>
                              filteredOptionsSalesRegion()?.some((region) => region.title === item),
                            )

                            setValue('codigosRegiaoVenda', newRegion.join())
                          }}
                          optionsList={optionsSalesTeam?.map((item) => item.title)}
                          selectedOptions={
                            typeof field.value !== 'string'
                              ? field.value
                              : splittingString(field.value)
                          }
                        />
                      )}
                    />
                  </S.FormColumn>
                  <S.FormColumn>
                    {labelField('Escritório', 'escritoriosVenda')}
                    <Controller
                      control={control}
                      name="escritoriosVenda"
                      render={({ field }) => (
                        <Multiselect
                          {...field}
                          onChange={(value) => field.onChange(value.join())}
                          optionsList={optionsOffices}
                          selectedOptions={
                            typeof field.value !== 'string'
                              ? field.value
                              : splittingString(field.value)
                          }
                        />
                      )}
                    />
                  </S.FormColumn>
                  <S.FormColumn>
                    {labelField('Região de vendas', 'codigosRegiaoVenda')}
                    <Controller
                      control={control}
                      name="codigosRegiaoVenda"
                      render={({ field }) => (
                        <Multiselect
                          {...field}
                          onChange={(value) => field.onChange(value.join())}
                          optionsList={filteredOptionsSalesRegion()?.map((item) => item.title)}
                          selectedOptions={
                            typeof field.value !== 'string'
                              ? field.value
                              : splittingString(field.value)
                          }
                        />
                      )}
                    />
                  </S.FormColumn>
                  <S.FormColumn>
                    <Controller
                      control={control}
                      name="codigosSapCliente"
                      render={({ field }) => (
                        <BatchSearch
                          {...field}
                          label="Código SAP"
                          onClear={() => reset({ ...getValues(), codigosSapCliente: '' })}
                        />
                      )}
                    />
                  </S.FormColumn>
                  <S.FormColumn>
                    <Controller
                      control={control}
                      name="razoesSociaisCliente"
                      render={({ field }) => (
                        <BatchSearch
                          label="Razão Social"
                          onClear={() => reset({ ...getValues(), razoesSociaisCliente: '' })}
                          {...field}
                        />
                      )}
                    />
                  </S.FormColumn>
                  <S.FormColumn>
                    <Controller
                      control={control}
                      name="numerosOrdemVenda"
                      render={({ field }) => (
                        <BatchSearch
                          label="OV portal"
                          onClear={() => reset({ ...getValues(), numerosOrdemVenda: '' })}
                          {...field}
                        />
                      )}
                    />
                  </S.FormColumn>
                </S.FormRow>
                <S.FormRow>
                  <S.FormColumn>
                    {labelField('Material', 'material')}
                    <Controller
                      control={control}
                      name="material"
                      render={({ field }) => <Input {...field} />}
                    />
                  </S.FormColumn>
                  <S.FormColumn>
                    {labelField('Tipo de material', 'tiposMaterial')}
                    <Controller
                      control={control}
                      name="tiposMaterial"
                      render={({ field }) => (
                        <Multiselect
                          {...field}
                          onChange={(value) => field.onChange(value.join())}
                          optionsList={['Fora de linha', 'Em linha']}
                          selectedOptions={
                            typeof field.value !== 'string'
                              ? field.value
                              : splittingString(field.value)
                          }
                        />
                      )}
                    />
                  </S.FormColumn>
                  <S.FormColumn>
                    {labelField('Tipo de OV', 'tiposOrdemVenda')}
                    <Controller
                      control={control}
                      name="tiposOrdemVenda"
                      render={({ field }) => (
                        <Multiselect
                          {...field}
                          onChange={(value) => field.onChange(value.join(','))}
                          optionsList={optionsOvTypes}
                          selectedOptions={
                            typeof field.value !== 'string'
                              ? field.value
                              : splittingString(field.value)
                          }
                        />
                      )}
                    />
                  </S.FormColumn>
                  <S.FormColumn>
                    {labelField('Mês cota', 'mesesCota')}
                    <Controller
                      control={control}
                      name="mesesCota"
                      render={({ field }) => (
                        <Multiselect
                          {...field}
                          onChange={(value) => {
                            field.onChange(value.join(','))
                          }}
                          optionsList={optionsMesCota}
                          selectedOptions={
                            typeof field.value !== 'string'
                              ? field.value
                              : splittingString(field.value)
                          }
                        />
                      )}
                    />
                  </S.FormColumn>
                  <S.FormColumn>
                    {labelField('Causa', 'causas')}
                    <Controller
                      control={control}
                      name="causas"
                      render={({ field }) => (
                        <Multiselect
                          {...field}
                          onChange={(value) => field.onChange(value.join())}
                          optionsList={['Prazo', 'Desconto', 'Prazo + Desconto', 'Bonificação']}
                          selectedOptions={
                            typeof field.value !== 'string'
                              ? field.value
                              : splittingString(field.value)
                          }
                        />
                      )}
                    />
                  </S.FormColumn>
                  <S.FormColumn>
                    {labelField('Aprovação', 'statusAprovacao')}
                    <Controller
                      control={control}
                      name="statusAprovacao"
                      render={({ field }) => (
                        <Multiselect
                          {...field}
                          onChange={(value) => field.onChange(value.join())}
                          optionsList={['Aprovado', 'Não aprovado']}
                          selectedOptions={
                            typeof field.value !== 'string'
                              ? field.value
                              : splittingString(field.value)
                          }
                        />
                      )}
                    />
                  </S.FormColumn>
                </S.FormRow>
                <S.FormRow className="flexEnd">
                  <S.SubColumn>
                    {labelField('Hierarquia', 'hierarquiaCliente')}
                    <S.WrapToggleHierarchy>
                      <Controller
                        control={control}
                        name="hierarquiaCliente"
                        render={({ field }) => (
                          <Toggle
                            {...field}
                            disabled={disabledHierarquia}
                            processChange={field.onBlur}
                            setValue={(value) => field.onChange(value.toString())}
                            value={field.value === 'true'}
                          />
                        )}
                      />
                    </S.WrapToggleHierarchy>
                  </S.SubColumn>
                  <S.FormColumn className="lineColumn">
                    <SlimButton
                      icon={<Icon icon="clear-all" size="20" />}
                      id="filters-clear-button"
                      onClick={handleClearFilter}
                      type="button"
                      variant="secondary"
                    />
                    <SlimButton content="Buscar" type="submit" variant="primary" />
                  </S.FormColumn>
                </S.FormRow>
              </S.AccordionContent>
            </S.FormContent>
          </S.AccordionFilter>
          <ModalReport
            open={openReportModal}
            setHistoryRequestModal={setOpenHistoryRequestModal}
            setOpen={setOpenReportModal}
          />
          <Modal
            buttonAction={() => setOpenHistoryRequestModal(false)}
            buttonText="Concluir"
            description="O sistema iniciou a geração do relatório de histórico de NDs para o período solicitado. Assim que o processo for concluído, uma notificação será enviada para o seu email."
            modalWidth={456}
            open={openHistoryRequestModal}
            setOpen={setOpenHistoryRequestModal}
            showClose={false}
            title={<Alert fullWidth kind="success" title="Geração de histórico de NDs iniciada!" />}
          />
        </S.Form>
      </FormProvider>
    </S.CardFilter>
  )
}
